import axios, { AxiosInstance } from 'axios';

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || '',
  headers: { 'Content-Type': 'application/json' },
});

/** Esito di un refresh: `scaduta` se il server rifiuta la sessione,
 *  `errore` se non risponde (la sessione resta: si riprova dopo). */
export type EsitoRefresh = 'ok' | 'scaduta' | 'errore';

// Token store — set by AuthProvider on mount; avoids circular dep with context
const _store = {
  getToken: (): string | null => null,
  refresh: async (): Promise<EsitoRefresh> => 'scaduta',
  logout: () => {},
};

/** Le chiamate di accesso rispondono 401 per credenziali o token sbagliati:
 *  per loro non si tenta il refresh. */
const ACCESSO = /\/api\/auth\/(login|refresh|logout)$/;

export function registerApiAuth(
  getToken: () => string | null,
  refresh: () => Promise<EsitoRefresh>,
  logout: () => void,
) {
  _store.getToken = getToken;
  _store.refresh = refresh;
  _store.logout = logout;
}

api.interceptors.request.use((config) => {
  // Se il chiamante ha già impostato esplicitamente un Authorization
  // header (es. token admin di backup durante l'impersonazione, vedi
  // AdminContext.getAdminToken), non sovrascriverlo con quello attivo.
  if (!config.headers.Authorization) {
    const token = _store.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry && !ACCESSO.test(original.url ?? '')) {
      original._retry = true;
      const esito = await _store.refresh();
      if (esito === 'ok') {
        original.headers.Authorization = `Bearer ${_store.getToken()}`;
        return api(original);
      }
      if (esito === 'scaduta') _store.logout();
    }
    return Promise.reject(error);
  },
);

export default api;
